import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Mail, ArrowLeft, RefreshCw, Send, CalendarDays, MapPin,
  Save, Eye, ImagePlus, Bold, Italic, List, ListOrdered,
  Heading3, Link as LinkIcon, Undo2, Quote, Trash2, Inbox,
} from "lucide-react";
import { format, parseISO } from "date-fns";

/* ---------------------------------------------------------------
 * Minimal rich-text editor for the admin monthly-digest update.
 * Uses contentEditable + document.execCommand for a toolbar that
 * produces clean semantic HTML (p / strong / em / h3 / ul / ol / li / a / img).
 * Submitted HTML is sanitised server-side by bleach, so this editor
 * only needs to produce markup — it doesn't need to trust itself.
 * --------------------------------------------------------------- */
const Toolbar = ({ exec, onImage }) => {
  const Btn = ({ cmd, arg, icon: Icon, label }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-8 w-8 p-0"
      title={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(cmd, arg)}
      data-testid={`digest-toolbar-${cmd}${arg ? `-${arg.replace(/\W/g, "")}` : ""}`}
    >
      <Icon className="w-4 h-4" />
    </Button>
  );
  return (
    <div className="flex items-center gap-1 p-2 border-b border-white/10 bg-black/20 rounded-t-md flex-wrap">
      <Btn cmd="bold" icon={Bold} label="Bold" />
      <Btn cmd="italic" icon={Italic} label="Italic" />
      <Btn cmd="formatBlock" arg="<h3>" icon={Heading3} label="Heading" />
      <Btn cmd="formatBlock" arg="<blockquote>" icon={Quote} label="Quote" />
      <Btn cmd="insertUnorderedList" icon={List} label="Bullet list" />
      <Btn cmd="insertOrderedList" icon={ListOrdered} label="Numbered list" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        title="Insert link"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const url = window.prompt("Link URL");
          if (url) exec("createLink", url);
        }}
        data-testid="digest-toolbar-link"
      >
        <LinkIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        title="Insert photo at cursor"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onImage}
        data-testid="digest-toolbar-image"
      >
        <ImagePlus className="w-4 h-4 text-amber-400" />
      </Button>
      <div className="flex-1" />
      <Btn cmd="undo" icon={Undo2} label="Undo" />
    </div>
  );
};

const RichEditor = ({ value, onChange, onImage, editorRef, disabled }) => {
  // Populate on first mount or when value changes externally (e.g. after load)
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const exec = (cmd, arg) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    const html = editorRef.current?.innerHTML || "";
    onChange(html);
  };

  return (
    <div className="rounded-md border border-white/10 bg-black/10">
      <Toolbar exec={exec} onImage={onImage} />
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        className="p-4 min-h-[320px] max-h-[560px] overflow-y-auto text-sm leading-relaxed focus:outline-none prose prose-invert prose-sm max-w-none"
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        onPaste={(e) => {
          // Strip pasted formatting to keep markup clean
          e.preventDefault();
          const text = e.clipboardData.getData("text/plain");
          document.execCommand("insertText", false, text);
        }}
        data-testid="digest-editor"
      />
    </div>
  );
};

/* ---------------- Events preview (unchanged from the old page) ---------------- */
const EventRow = ({ event }) => {
  const cancelled = (event.status || "").toLowerCase() === "cancelled";
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30 border border-white/5" data-testid={`digest-event-${event.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`font-medium text-sm truncate ${cancelled ? "line-through opacity-70" : ""}`}>{event.title}</p>
          <Badge variant="outline" className="text-[10px] capitalize">{event.event_type}</Badge>
          {cancelled && <Badge variant="outline" className="bg-red-500/15 text-red-400 border-transparent text-[10px]">Cancelled</Badge>}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
          {event.start_time && (
            <span className="flex items-center gap-1">
              <CalendarDays className="w-3 h-3" />
              {format(parseISO(event.start_time), "d MMM yyyy HH:mm")}
            </span>
          )}
          {event.location && (
            <span className="flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3" />
              {event.location}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const EventsSection = ({ title, subtitle, items }) => (
  <Card className="glass-card">
    <CardHeader className="border-b border-white/5 pb-4">
      <CardTitle className="font-heading text-lg">{title}</CardTitle>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
    </CardHeader>
    <CardContent className="p-4 space-y-2">
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">Nothing to show yet.</p>
      ) : (
        items.map((e) => <EventRow key={e.id} event={e} />)
      )}
    </CardContent>
  </Card>
);

/* ---------------- Page ---------------- */
const AdminMonthlyDigest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Draft state
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ title: "", body_html: "", photos: [] });
  const [titleDraft, setTitleDraft] = useState("");
  const [bodyDraft, setBodyDraft] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Events preview + last-sent
  const [events, setEvents] = useState({ new_events: [], upcoming_events: [], future_events: [], last_sent: null });

  // Email preview
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  // History
  const [history, setHistory] = useState([]);

  // Sending
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);

  // Active tab (so we know when to auto-fetch preview)
  const [tab, setTab] = useState("compose");

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, ev, h] = await Promise.all([
        apiClient.get("/band/monthly-digest/draft"),
        apiClient.get("/band/monthly-digest-preview"),
        apiClient.get("/band/monthly-digest/history"),
      ]);
      setDraft(d.data);
      setTitleDraft(d.data?.title || "");
      setBodyDraft(d.data?.body_html || "");
      setEvents(ev.data || {});
      setHistory(h.data || []);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load monthly digest");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Auto-fetch email preview when switching to that tab
  useEffect(() => {
    if (tab === "preview") {
      (async () => {
        setLoadingPreview(true);
        try {
          // Save dirty changes first so the preview reflects them
          if (dirty) await save(true);
          const r = await apiClient.get("/band/monthly-digest/preview-html");
          setPreviewHtml(r.data?.html || "");
          setPreviewSubject(r.data?.subject || "");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Failed to load preview");
        } finally {
          setLoadingPreview(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const onTitleChange = (v) => {
    setTitleDraft(v);
    setDirty(true);
  };
  const onBodyChange = (v) => {
    setBodyDraft(v);
    setDirty(true);
  };

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const r = await apiClient.put("/band/monthly-digest/draft", {
        title: titleDraft,
        body_html: bodyDraft,
      });
      setDraft(r.data);
      setDirty(false);
      if (!silent) toast.success("Draft saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const openImagePicker = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be 5 MB or smaller");
      return;
    }

    const form = new FormData();
    form.append("file", file);
    const saving = toast.loading(`Uploading ${file.name}...`);
    try {
      const res = await apiClient.post("/band/monthly-digest/draft/photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.dismiss(saving);
      toast.success("Photo uploaded");
      const src = `${API}/digest-photos/${res.data.id}`;
      // Insert at cursor in the editor as a styled img
      editorRef.current?.focus();
      const imgHtml = `<img src="${src}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:12px 0;" />`;
      document.execCommand("insertHTML", false, imgHtml);
      const html = editorRef.current?.innerHTML || "";
      onBodyChange(html);
      // Refresh draft photo list
      const d = await apiClient.get("/band/monthly-digest/draft");
      setDraft(d.data);
    } catch (err) {
      toast.dismiss(saving);
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const deletePhoto = async (photoId) => {
    if (!confirm("Remove this photo? The image will be removed from the draft and no longer appear in the email.")) return;
    try {
      await apiClient.delete(`/band/monthly-digest/draft/photo/${photoId}`);
      // Also strip <img src="/api/digest-photos/{id}"> occurrences from the body
      const src = `${API}/digest-photos/${photoId}`;
      const cleaned = bodyDraft
        .replace(new RegExp(`<img[^>]*src=["']${src.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}["'][^>]*>`, "gi"), "")
        .trim();
      setBodyDraft(cleaned);
      setDirty(true);
      toast.success("Photo removed");
      const d = await apiClient.get("/band/monthly-digest/draft");
      setDraft(d.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove photo");
    }
  };

  const sendTest = async () => {
    if (dirty) await save(true);
    if (!confirm(`Send a test copy of the digest to ${user?.email}?`)) return;
    setTesting(true);
    try {
      const r = await apiClient.post("/band/monthly-digest/test-send");
      toast.success(r.data?.message || "Test sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test");
    } finally {
      setTesting(false);
    }
  };

  const sendToAll = async () => {
    if (dirty) await save(true);
    if (!confirm("Send this monthly digest to ALL band members now? The draft will be archived and reset afterwards.")) return;
    setSending(true);
    try {
      const r = await apiClient.post("/band/monthly-digest/send");
      toast.success(r.data?.message || "Digest sent");
      await loadAll();
      setTab("compose");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send digest");
    } finally {
      setSending(false);
    }
  };

  const photoCount = draft.photos?.length || 0;
  const hasContent = useMemo(
    () => !!(titleDraft.trim() || bodyDraft.replace(/<[^>]+>/g, "").trim()),
    [titleDraft, bodyDraft]
  );

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl" data-testid="admin-monthly-digest-page">
      <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="admin-digest-back">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Admin
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight">Monthly Member Digest</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Write a band update, add photos, preview the full email, then send on your schedule.
              The auto-generated events digest is always appended at the end.
            </p>
            {events.last_sent && (
              <p className="text-xs text-muted-foreground mt-2">
                Last sent: {format(parseISO(events.last_sent), "d MMM yyyy, HH:mm")}
              </p>
            )}
            {draft.updated_at && (
              <p className="text-xs text-muted-foreground">
                Draft last saved {format(parseISO(draft.updated_at), "d MMM yyyy, HH:mm")}
                {draft.last_edited_by_name ? ` by ${draft.last_edited_by_name}` : ""}
                {dirty && <span className="ml-2 text-amber-400">• unsaved changes</span>}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => save(false)}
            disabled={!dirty || saving}
            data-testid="digest-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save draft"}
          </Button>
          <Button
            variant="outline"
            onClick={sendTest}
            disabled={testing}
            data-testid="digest-test-send-btn"
          >
            <Eye className="w-4 h-4 mr-2" />
            {testing ? "Sending..." : "Send test to me"}
          </Button>
          <Button
            onClick={sendToAll}
            disabled={sending}
            data-testid="digest-send-all-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? "Sending..." : "Send to all members"}
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <div className="overflow-x-auto">
        <TabsList data-testid="digest-tabs">
          <TabsTrigger value="compose" data-testid="digest-tab-compose">
            Your update
            {hasContent && <Badge variant="outline" className="ml-2 bg-emerald-500/15 text-emerald-300 border-transparent text-[10px]">draft</Badge>}
          </TabsTrigger>
          <TabsTrigger value="events" data-testid="digest-tab-events">
            Events
            <Badge variant="outline" className="ml-2 text-[10px]">
              {(events.new_events?.length || 0) + (events.upcoming_events?.length || 0) + (events.future_events?.length || 0)}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="preview" data-testid="digest-tab-preview">
            Email preview
          </TabsTrigger>
          {history.length > 0 && (
            <TabsTrigger value="history" data-testid="digest-tab-history">
              History
              <Badge variant="outline" className="ml-2 text-[10px]">{history.length}</Badge>
            </TabsTrigger>
          )}
        </TabsList>
        </div>

        {/* ---------------- Compose tab ---------------- */}
        <TabsContent value="compose" className="space-y-4">
          <Card className="glass-card">
            <CardContent className="p-6 space-y-4">
              <div>
                <Label htmlFor="digest_title">Update title</Label>
                <Input
                  id="digest_title"
                  value={titleDraft}
                  onChange={(e) => onTitleChange(e.target.value)}
                  placeholder="e.g. May band news — Festival prep!"
                  maxLength={200}
                  data-testid="digest-title-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. Appears as a header in the email.
                </p>
              </div>

              <div>
                <Label>Message body</Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Use the toolbar to add formatting. Click the <ImagePlus className="w-3 h-3 inline" /> icon
                  to insert a photo at your cursor. HTML is auto-sanitised on save.
                </p>
                <RichEditor
                  editorRef={editorRef}
                  value={bodyDraft}
                  onChange={onBodyChange}
                  onImage={openImagePicker}
                  disabled={loading}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  data-testid="digest-photo-file-input"
                />
              </div>

              {photoCount > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Attached photos ({photoCount})
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {draft.photos.map((p) => (
                      <div
                        key={p.id}
                        className="relative group rounded-md overflow-hidden border border-white/10 bg-black/20 aspect-video"
                        data-testid={`digest-photo-${p.id}`}
                      >
                        <img
                          src={`${API}/digest-photos/${p.id}`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => deletePhoto(p.id)}
                          className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-red-300 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                          title="Remove photo"
                          data-testid={`digest-photo-remove-${p.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- Events tab ---------------- */}
        <TabsContent value="events" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            These events are added automatically at the bottom of the email — concerts (any status)
            and cancelled rehearsals only. Manage them from the <strong>Events</strong> page.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <EventsSection
              title="New since last update"
              subtitle={`${events.new_events?.length || 0} item(s)`}
              items={events.new_events || []}
            />
            <EventsSection
              title="This month"
              subtitle={`${events.upcoming_events?.length || 0} item(s)`}
              items={events.upcoming_events || []}
            />
            <EventsSection
              title="Coming up (2-4 months)"
              subtitle={`${events.future_events?.length || 0} item(s)`}
              items={events.future_events || []}
            />
          </div>
        </TabsContent>

        {/* ---------------- Email preview tab ---------------- */}
        <TabsContent value="preview" className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm">
              <span className="text-muted-foreground">Subject:</span>{" "}
              <span dangerouslySetInnerHTML={{ __html: previewSubject || "—" }} />
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                setLoadingPreview(true);
                try {
                  if (dirty) await save(true);
                  const r = await apiClient.get("/band/monthly-digest/preview-html");
                  setPreviewHtml(r.data?.html || "");
                  setPreviewSubject(r.data?.subject || "");
                } finally {
                  setLoadingPreview(false);
                }
              }}
              data-testid="digest-preview-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingPreview ? "animate-spin" : ""}`} />
              Refresh preview
            </Button>
          </div>
          <div className="rounded-lg border border-white/10 overflow-hidden bg-black/20">
            {loadingPreview ? (
              <div className="p-12 text-center text-muted-foreground">Rendering preview...</div>
            ) : (
              <iframe
                title="Email preview"
                srcDoc={previewHtml}
                className="w-full bg-white"
                style={{ minHeight: "720px", border: "0" }}
                data-testid="digest-preview-iframe"
                sandbox=""
              />
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            This is exactly what members will see. Click <strong>Send test to me</strong> above
            to deliver a copy to your inbox, or <strong>Send to all members</strong> to send it for real.
          </p>
        </TabsContent>

        {/* ---------------- History tab ---------------- */}
        {history.length > 0 && (
          <TabsContent value="history" className="space-y-3">
            <Card className="glass-card">
              <CardHeader className="border-b border-white/5 pb-4">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Inbox className="w-5 h-5 text-muted-foreground" />
                  Previous digests
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2">
                {history.map((h) => (
                  <div
                    key={h.id}
                    className="flex items-center justify-between p-3 bg-white/[0.02] rounded-md border border-white/[0.04]"
                    data-testid={`digest-history-${h.id}`}
                  >
                    <div>
                      <p className="font-medium text-sm">
                        {h.title || <span className="italic text-muted-foreground">(no update title)</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Sent {format(parseISO(h.sent_at), "d MMM yyyy, HH:mm")}
                        {h.sent_by_name ? ` · ${h.sent_by_name}` : ""}
                        {` · ${h.sent_count || 0} delivered`}
                        {h.failed_count > 0 && <span className="text-red-400"> · {h.failed_count} failed</span>}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {(h.photos?.length || 0)} photo{(h.photos?.length || 0) !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default AdminMonthlyDigest;
